import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, WhatsappLogo, EnvelopeSimple, InstagramLogo } from '@phosphor-icons/react';
import { ContactForm } from '@/components/ContactForm';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const Contato = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-white" data-testid="contato-hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#2A2A2A] playfair tracking-tight mb-6">
              Contato
            </h1>
            <p className="text-xl text-[#737373] leading-relaxed">
              Apresente sua situação à RDC Advogados e receba orientação inicial com clareza, discrição e
              seriedade profissional.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="contato-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div {...fadeUp}>
              <h2 className="text-3xl font-bold text-[#2A2A2A] playfair mb-6">RDC Advogados</h2>
              <p className="text-lg text-[#737373] leading-relaxed mb-8">
                Se você precisa de orientação jurídica em Direito Trabalhista, ações contra Plano de Saúde, BPC/LOAS ou Defesa em Busca e Apreensão, entre em contato com o escritório. O atendimento é conduzido com análise responsável,
                leitura técnica do caso e comunicação objetiva sobre os caminhos jurídicos possíveis.
              </p>

              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start space-x-4" data-testid="contact-address">
                  <MapPin size={24} weight="duotone" className="text-[#A98C67] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-[#2A2A2A] mb-1">Endereço</h3>
                    <p className="text-[#737373]">
                      Rua São José, nº 46, Grupo 506/507<br />
                      Centro - Rio de Janeiro/RJ<br />
                      CEP: 20.010-020
                    </p>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-start space-x-4">
                  <WhatsappLogo size={24} weight="duotone" className="text-[#A98C67] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-[#2A2A2A] mb-1">WhatsApp</h3>
                    <a
                      href="https://wa.me/552128880258"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#737373] hover:text-[#2A2A2A] transition-colors"
                      data-testid="contact-whatsapp-link"
                    >
                      +55 21 2888-0258
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <EnvelopeSimple size={24} weight="duotone" className="text-[#A98C67] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-[#2A2A2A] mb-1">E-mail</h3>
                    <a
                      href="mailto:contato.rdcadvogados@gmail.com"
                      className="text-[#737373] hover:text-[#2A2A2A] transition-colors"
                      data-testid="contact-email-link"
                    >
                      contato.rdcadvogados@gmail.com
                    </a>
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex items-start space-x-4">
                  <InstagramLogo size={24} weight="duotone" className="text-[#A98C67] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-[#2A2A2A] mb-1">Instagram</h3>
                    <a
                      href="https://www.instagram.com/advogadosrdc/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#737373] hover:text-[#2A2A2A] transition-colors"
                      data-testid="contact-instagram-link"
                    >
                      @advogadosrdc
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div {...fadeUp} transition={{ delay: 0.2, duration: 0.6 }}>
              <div className="bg-white border border-[#2A2A2A]/10 rounded-sm p-8">
                <h3 className="text-2xl font-bold text-[#2A2A2A] playfair mb-6">Envie sua mensagem</h3>
                <p className="text-[#737373] mb-8">
                  Preencha os campos abaixo com seus dados e uma breve descrição da situação. As informações
                  iniciais auxiliam na compreensão do caso e no direcionamento adequado do atendimento.
                </p>
                <ContactForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 bg-white" data-testid="contato-trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-6">
              Atendimento com discrição e atenção ao caso concreto
            </h2>
            <p className="text-lg text-[#737373] leading-relaxed">
              Cada situação é recebida com seriedade e analisada de forma individual. O objetivo do contato
              inicial é compreender o contexto apresentado e orientar, com clareza, os próximos passos
              juridicamente adequados.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="contato-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold playfair mb-6">Fale com a RDC Advogados</h2>
            <p className="text-lg text-white/80 mb-8">
              Uma orientação jurídica segura começa com uma análise bem conduzida. Entre em contato e apresente o
              seu caso.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://wa.me/552128880258" target="_blank" rel="noopener noreferrer">
                <button
                  data-testid="contato-final-whatsapp-button"
                  className="bg-[#25D366] text-white hover:bg-[#20BA5A] rounded-sm transition-colors duration-200 font-medium px-8 py-3 text-base inline-flex items-center space-x-2"
                >
                  <WhatsappLogo size={20} weight="bold" />
                  <span>Falar pelo WhatsApp</span>
                </button>
              </a>
              <a href="mailto:contato.rdcadvogados@gmail.com">
                <button
                  data-testid="contato-final-email-button"
                  className="bg-transparent text-white border border-white/20 hover:bg-white/10 rounded-sm transition-colors duration-200 font-medium px-8 py-3 text-base inline-flex items-center space-x-2"
                >
                  <EnvelopeSimple size={20} weight="bold" />
                  <span>Enviar e-mail</span>
                </button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contato;